import { Assets, Rectangle, Texture } from 'pixi.js';
import {
  RUNTIME_STATIC_ATLAS_PAGE_MAX,
  type BakedStaticsManifest,
} from './bakedManifest';
import { staticAtlasPageUrl } from './staticAssetUrls';
import { loadStaticsManifest } from './staticsManifest';

/**
 * Static tile textures keyed by static name (`tree`, ...), plus optional UO
 * foliage metadata from the same baked manifest.
 */
export type StaticTextures = Map<string, Texture> & {
  foliage?: ReadonlySet<string>;
};

/**
 * Slice every named static from the bounded atlas page declared by its manifest
 * entry.
 */
export function buildStaticTextures(
  manifest: BakedStaticsManifest,
  atlases: readonly Texture[],
): StaticTextures {
  for (const page of manifest.pages) {
    if (
      page.width > RUNTIME_STATIC_ATLAS_PAGE_MAX
      || page.height > RUNTIME_STATIC_ATLAS_PAGE_MAX
    ) {
      throw new Error(
        `statics page "${page.atlas}" is ${page.width}x${page.height}; `
        + `maximum is ${RUNTIME_STATIC_ATLAS_PAGE_MAX}`,
      );
    }
  }
  const textures: StaticTextures = new Map();
  textures.foliage = new Set(
    manifest.statics.filter((item) => item.foliage).map((item) => item.name),
  );
  for (const s of manifest.statics) {
    const atlas = atlases[s.page];
    if (!atlas) {
      throw new Error(
        `static "${s.name}" references missing atlas page ${String(s.page)}`,
      );
    }
    textures.set(
      s.name,
      new Texture({ source: atlas.source, frame: new Rectangle(s.x, s.y, s.w, s.h) }),
    );
  }
  return textures;
}

/**
 * Load every bounded statics page and reconstruct the named texture map.
 */
export async function loadStaticTextures(): Promise<StaticTextures> {
  const manifest = await loadStaticsManifest();
  const pageUrls = manifest.pages.map((page) => {
    const url = staticAtlasPageUrl(page.atlas);
    if (!url) {
      throw new Error(`no bundled statics atlas page "${page.atlas}"`);
    }
    return url;
  });
  const atlases: Texture[] = [];
  for (const url of pageUrls) {
    const atlas = await Assets.load<Texture>(url);
    atlas.source.scaleMode = 'nearest';
    atlases.push(atlas);
  }
  return buildStaticTextures(manifest, atlases);
}
